import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import Modal from './Modal';
import { ThemeContext } from '../contexts/ThemeContext';
import { ResultsContext } from '../contexts/ResultsContext';
import { useTournamentAppConfiguration } from '../utils/tournamentconfig/TournamentConfiguration';
import { calculateTournamentRankings } from '../utils/tournamentScoring';
import { filterIgnoredGames } from '../utils/ignoredGames';
import './GameCardAnimationModal.css';

// Helper to draw text with dynamic font size scaling to fit a width
const drawScaledText = (ctx, text, x, y, maxWidth, initialFontSize, isBold = true, textAlign = 'center') => {
  let fontSize = initialFontSize;
  ctx.font = `${isBold ? 'bold ' : ''}${fontSize}px Oswald, Arial, sans-serif`;
  
  while (ctx.measureText(text).width > maxWidth && fontSize > 14) {
    fontSize -= 1;
    ctx.font = `${isBold ? 'bold ' : ''}${fontSize}px Oswald, Arial, sans-serif`;
  }
  
  const originalAlign = ctx.textAlign;
  ctx.textAlign = textAlign;
  ctx.fillText(text, x, y);
  ctx.textAlign = originalAlign;
  return fontSize;
};

const formatShortDate = (dateString) => {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return dateString;
  }
};

// Helper to draw an image to fit a rectangle while preserving aspect ratio (object-fit: cover)
const drawImageCover = (ctx, img, x, y, w, h) => {
  if (!img || img.width === 0 || img.height === 0) {
    // Draw an elegant pattern/gradient placeholder if image is missing
    const gradient = ctx.createLinearGradient(x, y, x + w, y + h);
    gradient.addColorStop(0, '#1f2937');
    gradient.addColorStop(1, '#111827');
    ctx.fillStyle = gradient;
    ctx.fillRect(x, y, w, h);
    return;
  }
  
  const imgRatio = img.width / img.height;
  const rectRatio = w / h;
  
  let sx, sy, sWidth, sHeight;
  
  if (imgRatio > rectRatio) {
    sHeight = img.height;
    sWidth = img.height * rectRatio;
    sx = (img.width - sWidth) / 2;
    sy = 0;
  } else {
    sWidth = img.width;
    sHeight = img.width / rectRatio;
    sx = 0;
    sy = (img.height - sHeight) / 2;
  }
  
  ctx.drawImage(img, sx, sy, sWidth, sHeight, x, y, w, h);
};

// Helper to draw a rounded rectangle
const drawRoundedRect = (ctx, x, y, w, h, r) => {
  if (w < 2 * r) r = w / 2;
  if (h < 2 * r) r = h / 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

function GameCardAnimationModal({ isOpen, onClose, username, overallRank }) {
  const { theme } = useContext(ThemeContext);
  const { results } = useContext(ResultsContext);
  const tournamentAppConfiguration = useTournamentAppConfiguration();

  // Settings states
  const [aspectRatio, setAspectRatio] = useState('square'); // 'square', 'stories', 'landscape'
  const [sortOrder, setSortOrder] = useState('default'); // 'default', 'rank', 'alpha'
  const [orderByHighestRank, setOrderByHighestRank] = useState(true);
  const [showRankedOnly, setShowRankedOnly] = useState(true);

  // Playback states
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Loading & Video recording states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordProgress, setRecordProgress] = useState(0);

  // Refs for rendering
  const canvasRef = useRef(null);
  const requestRef = useRef(null);
  const prevTimeRef = useRef(null);
  const cardElapsedRef = useRef(0);
  const [gamesList, setGamesList] = useState([]);
  const loadedImagesRef = useRef({});
  const activeGamesRef = useRef([]);

  // Transition parameters
  const slideDuration = 3000; // 3 seconds
  const transitionDuration = 300; // 300ms fade transition

  // Offscreen canvases for rendering transitions
  const offscreenCanvas1 = useRef(null);
  const offscreenCanvas2 = useRef(null);

  // Helper to calculate percent lead for a game leaderboard
  const getUserLead = useCallback((game, userName) => {
    if (!game || !game.leaderboard || game.leaderboard.length === 0 || !userName) return 0;
    const userIndex = game.leaderboard.findIndex(
      entry => entry.userName.toLowerCase() === userName.toLowerCase()
    );
    if (userIndex === -1) return 0;
    
    // If user is at the bottom, lead is 0%
    if (userIndex + 1 >= game.leaderboard.length) return 0;
    
    const userScore = game.leaderboard[userIndex].score;
    const nextScore = game.leaderboard[userIndex + 1].score;
    
    if (nextScore <= 0) return 0;
    
    return ((userScore - nextScore) / nextScore) * 100;
  }, []);

  // Filter and sort games based on options
  const getProcessedGames = useCallback(() => {
    if (!gamesList || gamesList.length === 0) return [];

    let processed = [...gamesList];

    // Filter
    if (showRankedOnly) {
      processed = processed.filter(g => g.isRanked);
    }

    // Sort
    if (orderByHighestRank) {
      processed.sort((a, b) => {
        // Unranked games go to the end
        if (a.isRanked !== b.isRanked) {
          return a.isRanked ? -1 : 1;
        }
        if (!a.isRanked && !b.isRanked) {
          return a.title.localeCompare(b.title);
        }
        // Both are ranked
        if (a.rank !== b.rank) {
          return a.rank - b.rank; // Ascending: rank 1, then rank 2...
        }
        // Same rank: sort by percent lead descending
        const leadA = getUserLead(a, username);
        const leadB = getUserLead(b, username);
        if (leadA !== leadB) {
          return leadB - leadA; // Descending: higher lead first
        }
        // Fallback to alphabetical
        return a.title.localeCompare(b.title);
      });
    } else if (sortOrder === 'alpha') {
      processed.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortOrder === 'rank') {
      processed.sort((a, b) => {
        const rankA = a.isRanked ? a.rank : Infinity;
        const rankB = b.isRanked ? b.rank : Infinity;
        if (rankA !== rankB) return rankA - rankB;
        return a.title.localeCompare(b.title);
      });
    }

    return processed;
  }, [gamesList, showRankedOnly, sortOrder, orderByHighestRank, username, getUserLead]);

  // Load results and images
  useEffect(() => {
    if (!isOpen || !results || !results.games || !username) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const filteredGames = filterIgnoredGames(results.games, results.tournamentId);

    // Centralized rankings calculation to find best/worst rank
    const rankings = calculateTournamentRankings(filteredGames, tournamentAppConfiguration, username, '-');
    const bestRankVal = rankings.bestRank === Infinity ? null : rankings.bestRank;
    const worstRankVal = rankings.worstRank === 0 ? null : rankings.worstRank;

    const userGames = filteredGames.map(game => {
      const userEntry = game.leaderboard?.find(entry =>
        entry.userName.toLowerCase() === username.toLowerCase()
      );
      
      const isRanked = !!userEntry;
      return {
        title: game.title,
        image: game.image,
        rank: userEntry?.rank,
        score: userEntry?.score,
        initials: userEntry?.initials,
        points: userEntry?.points,
        isRanked,
        leaderboard: game.leaderboard || [],
        isHighestRank: isRanked && bestRankVal !== null && userEntry.rank === bestRankVal,
        isLowestRank: isRanked && worstRankVal !== null && userEntry.rank === worstRankVal
      };
    });

    setGamesList(userGames);
    setCurrentIndex(0);
    cardElapsedRef.current = 0;

    // Pre-load all unique game images
    const uniqueImages = [...new Set(userGames.map(g => g.image))].filter(Boolean);

    if (uniqueImages.length === 0) {
      setLoading(false);
      return;
    }

    const loadImage = (url) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          loadedImagesRef.current[url] = img;
          resolve();
        };
        img.onerror = () => {
          console.error(`Failed to load: ${url}`);
          // Resolve with null, will fallback to gradient placeholder in drawImageCover
          loadedImagesRef.current[url] = null;
          resolve();
        };
        img.src = url;
      });
    };

    Promise.all(uniqueImages.map(url => loadImage(url))).then(() => {
      setLoading(false);
    });

    return () => {
      loadedImagesRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, results, username]);

  const activeGames = useMemo(() => {
    const processed = getProcessedGames();
    activeGamesRef.current = processed;
    return processed;
  }, [getProcessedGames]);

  // Reset indices when activeGames list changes
  useEffect(() => {
    setCurrentIndex(0);
    cardElapsedRef.current = 0;
  }, [activeGames]);

  // Handle play/pause toggle
  const togglePlay = () => {
    setIsPlaying(prev => !prev);
    prevTimeRef.current = null;
  };

  // Handle restart slideshow
  const restartSlideshow = () => {
    setCurrentIndex(0);
    cardElapsedRef.current = 0;
    prevTimeRef.current = null;
  };

  // Determine aspect ratio canvas dimensions
  const getCanvasDimensions = (ratio) => {
    switch (ratio) {
      case 'stories': // 9:16 vertical
        return { w: 1080, h: 1920 };
      case 'landscape': // 16:9 widescreen
        return { w: 1920, h: 1080 };
      case 'square':
      default: // 1:1 square
        return { w: 1080, h: 1080 };
    }
  };

  // Render a specific card onto a canvas context
  const renderSingleCard = useCallback((ctx, game, width, height, progress, cardIndex = 0, totalCards = 0) => {
    // Reset context state to prevent state leaking between frames/renders
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;

    // Clear canvas
    ctx.fillStyle = '#080808';
    ctx.fillRect(0, 0, width, height);

    if (!game) {
      ctx.fillStyle = 'white';
      ctx.font = '24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No Game Card Selected', width / 2, height / 2);
      return;
    }

    let dateRange = '';
    if (results?.start || results?.end) {
      const startStr = results.start ? formatShortDate(results.start) : '';
      const endStr = results.end ? formatShortDate(results.end) : '';
      if (startStr && endStr) {
        if (startStr === endStr) {
          dateRange = startStr;
        } else {
          dateRange = `${startStr} - ${endStr}`;
        }
      } else {
        dateRange = startStr || endStr;
      }
    }

    if (aspectRatio === 'stories') {
      // ----------------------------------------------------
      // VERTICAL LAYOUT (9:16)
      // ----------------------------------------------------
      const margin = 60;
      const cardX = margin;
      const cardY = 140;
      const cardW = width - (margin * 2);
      const cardH = height - cardY - margin - 80;

      // Draw Top Overall Header
      ctx.fillStyle = 'white';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 6;
      drawScaledText(ctx, results?.title || 'Tournament', width / 2, 60, width - 80, 32, true, 'center');
      ctx.fillStyle = '#10B981';
      let subtitleText = `${username}  •  Overall: ${overallRank || '-'}`;
      if (dateRange) {
        subtitleText += `  •  ${dateRange}`;
      }
      drawScaledText(ctx, subtitleText, width / 2, 105, width - 80, 24, true, 'center');
      ctx.shadowBlur = 0;

      // Card border highlight based on status
      let borderColor = '#1f2937';
      if (game.isHighestRank) borderColor = '#10B981';
      else if (game.isLowestRank && game.isRanked) borderColor = '#F59E0B';
      else if (!game.isRanked) borderColor = '#EF4444';

      ctx.save();
      drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 24);
      ctx.clip();

      // Card Background
      ctx.fillStyle = '#121212';
      ctx.fillRect(cardX, cardY, cardW, cardH);

      // Image Area (Upper 55%)
      const imgH = cardH * 0.55;
      const imgUrl = game.image;
      const imgObj = loadedImagesRef.current[imgUrl];
      drawImageCover(ctx, imgObj, cardX, cardY, cardW, imgH);

      // Gradient overlay for titles
      const gradTop = ctx.createLinearGradient(cardX, cardY, cardX, cardY + 160);
      gradTop.addColorStop(0, 'rgba(0, 0, 0, 0.85)');
      gradTop.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradTop;
      ctx.fillRect(cardX, cardY, cardW, 160);

      const gradBot = ctx.createLinearGradient(cardX, cardY + imgH - 160, cardX, cardY + imgH);
      gradBot.addColorStop(0, 'rgba(0, 0, 0, 0)');
      gradBot.addColorStop(1, 'rgba(0, 0, 0, 0.85)');
      ctx.fillStyle = gradBot;
      ctx.fillRect(cardX, cardY + imgH - 160, cardW, 160);

      // Title
      ctx.fillStyle = 'white';
      drawScaledText(ctx, game.title, cardX + 35, cardY + 70, cardW - 70, 48, true, 'left');


      // Initials & Score Overlay
      if (game.isRanked) {
        ctx.fillStyle = '#10B981';
        drawScaledText(ctx, game.initials || '', cardX + 35, cardY + imgH - 30, 200, 36, true, 'left');
        if (game.score) {
          ctx.fillStyle = 'white';
          drawScaledText(ctx, game.score.toLocaleString(), cardX + cardW - (game.rank ? 180 : 35), cardY + imgH - 30, cardW - 250, 42, true, 'right');
        }
      }

      // Details section (Lower 45%)
      const detailsY = cardY + imgH;
      const detailsH = cardH - imgH;

      ctx.fillStyle = '#1e1e1e';
      ctx.fillRect(cardX + 25, detailsY + 25, cardW - 50, detailsH - 50);

      // Stats drawing inside container
      ctx.fillStyle = 'white';

      if (game.isRanked && game.rank === 1) {
        let textY = detailsY + 120;
        ctx.fillStyle = '#F59E0B'; // Gold
        drawScaledText(ctx, '🏆 GRAND CHAMPION', width / 2, textY, cardW - 110, 48, true, 'center');

        if (game.leaderboard.length > 1 && game.score) {
          ctx.fillStyle = '#10B981'; // Green ahead text
          const pctAhead = ((game.score - game.leaderboard[1].score) / game.leaderboard[1].score * 100).toFixed(1);
          drawScaledText(ctx, `(${pctAhead}% ahead)`, width / 2, textY + 50, cardW - 110, 28, true, 'center');
        }

        textY += 120;
        ctx.fillStyle = '#A0AEC0';
        drawScaledText(ctx, 'TOTAL PLAYERS:', width / 2, textY, cardW - 110, 26, false, 'center');
        ctx.fillStyle = 'white';
        drawScaledText(ctx, String(game.leaderboard.length), width / 2, textY + 45, cardW - 110, 36, true, 'center');
      } else {
        let textY = detailsY + 80;

        // Points & Players Row
        ctx.fillStyle = '#A0AEC0';
        drawScaledText(ctx, 'POINTS:', cardX + 55, textY, (cardW - 100) / 2, 26, false, 'left');
        drawScaledText(ctx, 'PLAYERS:', cardX + cardW / 2 + 30, textY, (cardW - 100) / 2, 26, false, 'left');
        
        const ptsVal = game.isRanked ? String(game.points ?? '-') : '-';
        ctx.fillStyle = '#10B981';
        drawScaledText(ctx, ptsVal, cardX + 55, textY + 45, (cardW - 100) / 2, 36, true, 'left');
        ctx.fillStyle = 'white';
        drawScaledText(ctx, String(game.leaderboard.length), cardX + cardW / 2 + 30, textY + 45, (cardW - 100) / 2, 36, true, 'left');

        textY += 120;

        // Top Score
        const topScoreEntry = game.leaderboard[0];
        ctx.fillStyle = '#A0AEC0';
        drawScaledText(ctx, 'TOP SCORE:', cardX + 55, textY, cardW - 110, 26, false, 'left');
        ctx.fillStyle = 'white';
        let topScoreText = 'N/A';
        if (topScoreEntry) {
          topScoreText = `${topScoreEntry.score.toLocaleString()} (${topScoreEntry.userName})`;
          if (game.isRanked && game.rank > 1 && game.score) {
            topScoreText += ` (+${((topScoreEntry.score - game.score) / game.score * 100).toFixed(1)}% needed)`;
          }
        }
        drawScaledText(ctx, topScoreText, cardX + 55, textY + 45, cardW - 110, 34, true, 'left');

        // Score to Beat
        let beatText = 'N/A';
        let beatScore = null;
        if (game.isRanked && game.rank > 1) {
          const nextRankEntry = game.leaderboard.find(e => e.rank === game.rank - 1);
          if (nextRankEntry) {
            beatScore = nextRankEntry.score;
            beatText = `${nextRankEntry.score.toLocaleString()} (Rank #${game.rank - 1})`;
            if (game.score) {
              beatText += ` (+${((nextRankEntry.score - game.score) / game.score * 100).toFixed(1)}% needed)`;
            }
          }
        } else if (!game.isRanked && game.leaderboard.length > 0) {
          const targetEntry = game.leaderboard.length >= 50 ? game.leaderboard[49] : game.leaderboard[game.leaderboard.length - 1];
          if (targetEntry) {
            beatScore = targetEntry.score;
            beatText = `${targetEntry.score.toLocaleString()} (Rank #${targetEntry.rank})`;
          }
        }
        if (beatScore !== null && beatScore !== topScoreEntry?.score) {
          textY += 120;
          ctx.fillStyle = '#A0AEC0';
          drawScaledText(ctx, 'SCORE TO BEAT:', cardX + 55, textY, cardW - 110, 26, false, 'left');
          ctx.fillStyle = '#F59E0B';
          drawScaledText(ctx, beatText, cardX + 55, textY + 45, cardW - 110, 34, true, 'left');
        }
      }

      // Rank Circular Badge (Moved here to draw on top of details container and raised slightly)
      if (game.isRanked && game.rank) {
        const badgeX = cardX + cardW - 90;
        const badgeY = cardY + imgH - 70;
        
        ctx.shadowColor = 'rgba(0,0,0,0.6)';
        ctx.shadowBlur = 8;
        
        ctx.fillStyle = '#10B981';
        ctx.beginPath();
        ctx.arc(badgeX, badgeY, 65, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 5;
        ctx.stroke();
        
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'white';
        drawScaledText(ctx, `#${game.rank}`, badgeX, badgeY + 20, 100, 56, true, 'center');
      }

      ctx.restore();

      // Card border
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 10;
      drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 24);
      ctx.stroke();

      // Page Indicator
      if (totalCards > 0) {
        ctx.fillStyle = '#A0AEC0';
        ctx.font = '20px Jost, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`Card ${cardIndex + 1} of ${totalCards}`, width / 2, height - 115);
      }

      // Progress bar at the bottom
      const progressW = width - (margin * 2);
      ctx.fillStyle = '#1f2937';
      drawRoundedRect(ctx, margin, height - 90, progressW, 12, 6);
      ctx.fill();

      ctx.fillStyle = '#10B981';
      drawRoundedRect(ctx, margin, height - 90, progressW * progress, 12, 6);
      ctx.fill();

      // Footer
      ctx.fillStyle = '#718096';
      ctx.font = '22px Jost, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Created with FliPal Tournament Companion', width / 2, height - 40);

    } else if (aspectRatio === 'landscape') {
      // ----------------------------------------------------
      // LANDSCAPE LAYOUT (16:9 - Split Screen)
      // ----------------------------------------------------
      const margin = 60;
      const cardX = margin;
      const cardY = margin;
      const cardW = width - (margin * 2);
      const cardH = height - (margin * 2) - 80;

      // Card border highlight based on status
      let borderColor = '#1f2937';
      if (game.isHighestRank) borderColor = '#10B981';
      else if (game.isLowestRank && game.isRanked) borderColor = '#F59E0B';
      else if (!game.isRanked) borderColor = '#EF4444';

      ctx.save();
      drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 24);
      ctx.clip();

      // Left Image Width = 48% of Card Width
      const leftW = cardW * 0.48;
      const rightW = cardW - leftW;
      
      const imgUrl = game.image;
      const imgObj = loadedImagesRef.current[imgUrl];
      drawImageCover(ctx, imgObj, cardX, cardY, leftW, cardH);

      // Left pane gradient
      const gradLeft = ctx.createLinearGradient(cardX, cardY, cardX + leftW, cardY);
      gradLeft.addColorStop(0, 'rgba(0, 0, 0, 0.4)');
      gradLeft.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      const gradBot = ctx.createLinearGradient(cardX, cardY + cardH - 180, cardX, cardY + cardH);
      gradBot.addColorStop(0, 'rgba(0, 0, 0, 0)');
      gradBot.addColorStop(1, 'rgba(0, 0, 0, 0.9)');
      ctx.fillStyle = gradBot;
      ctx.fillRect(cardX, cardY + cardH - 180, leftW, 180);

      // Title & Score overlay on Image
      ctx.fillStyle = 'white';
      drawScaledText(ctx, game.title, cardX + 35, cardY + 70, leftW - 70, 48, true, 'left');

      if (game.isRanked) {
        ctx.fillStyle = '#10B981';
        drawScaledText(ctx, game.initials || '', cardX + 35, cardY + cardH - 35, 150, 36, true, 'left');
        if (game.score) {
          ctx.fillStyle = 'white';
          drawScaledText(ctx, game.score.toLocaleString(), cardX + leftW - 35, cardY + cardH - 35, leftW - 220, 42, true, 'right');
        }
      }

      // Right Stats Pane
      const rightX = cardX + leftW;
      ctx.fillStyle = '#121212';
      ctx.fillRect(rightX, cardY, rightW, cardH);

      // Header inside Right Pane
      ctx.fillStyle = 'white';
      drawScaledText(ctx, results?.title || 'Tournament', rightX + 45, cardY + 70, rightW - 90, 36, true, 'left');
      ctx.fillStyle = '#10B981';
      let subtitleText = `${username}  •  Overall: ${overallRank || '-'}`;
      if (dateRange) {
        subtitleText += `  •  ${dateRange}`;
      }
      drawScaledText(ctx, subtitleText, rightX + 45, cardY + 115, rightW - 90, 24, true, 'left');

      // Right pane stats cards
      ctx.fillStyle = '#1c1c1e';
      ctx.fillRect(rightX + 45, cardY + 160, rightW - 90, cardH - 220);

      if (game.isRanked && game.rank === 1) {
        let textY = cardY + 240;
        ctx.fillStyle = '#F59E0B'; // Gold
        drawScaledText(ctx, '🏆 GRAND CHAMPION', rightX + rightW / 2, textY, rightW - 160, 48, true, 'center');

        if (game.leaderboard.length > 1 && game.score) {
          ctx.fillStyle = '#10B981';
          const pctAhead = ((game.score - game.leaderboard[1].score) / game.leaderboard[1].score * 100).toFixed(1);
          drawScaledText(ctx, `(${pctAhead}% ahead)`, rightX + rightW / 2, textY + 50, rightW - 160, 28, true, 'center');
        }

        textY += 130;
        ctx.fillStyle = '#A0AEC0';
        drawScaledText(ctx, 'TOTAL PLAYERS:', rightX + rightW / 2, textY, rightW - 160, 24, false, 'center');
        ctx.fillStyle = 'white';
        drawScaledText(ctx, String(game.leaderboard.length), rightX + rightW / 2, textY + 45, rightW - 160, 38, true, 'center');
      } else {
        // Stats rows
        let textY = cardY + 230;
        
        // Rank, Points, Players row
        ctx.fillStyle = '#A0AEC0';
        const colWidth = (rightW - 120) / 3;
        drawScaledText(ctx, 'GAME RANK', rightX + 80, textY, colWidth, 24, false, 'left');
        drawScaledText(ctx, 'POINTS', rightX + rightW / 3 + 40, textY, colWidth, 24, false, 'left');
        drawScaledText(ctx, 'TOTAL PLAYERS', rightX + (rightW / 3) * 2 + 10, textY, colWidth, 24, false, 'left');

        const rankText = game.isRanked ? `#${game.rank}` : 'Unranked';
        const ptsVal = game.isRanked ? String(game.points ?? '-') : '-';
        
        ctx.fillStyle = '#10B981';
        drawScaledText(ctx, rankText, rightX + 80, textY + 55, colWidth, 44, true, 'left');
        drawScaledText(ctx, ptsVal, rightX + rightW / 3 + 40, textY + 55, colWidth, 44, true, 'left');
        ctx.fillStyle = 'white';
        drawScaledText(ctx, String(game.leaderboard.length), rightX + (rightW / 3) * 2 + 10, textY + 55, colWidth, 44, true, 'left');

        textY += 150;

        // Top Score
        const topScoreEntry = game.leaderboard[0];
        ctx.fillStyle = '#A0AEC0';
        drawScaledText(ctx, 'TOP LEADERBOARD SCORE:', rightX + 80, textY, rightW - 130, 24, false, 'left');
        ctx.fillStyle = 'white';
        let topScoreText = 'N/A';
        if (topScoreEntry) {
          topScoreText = `${topScoreEntry.score.toLocaleString()} (${topScoreEntry.userName})`;
          if (game.isRanked && game.rank > 1 && game.score) {
            topScoreText += ` (+${((topScoreEntry.score - game.score) / game.score * 100).toFixed(1)}% needed)`;
          }
        }
        drawScaledText(ctx, topScoreText, rightX + 80, textY + 45, rightW - 130, 36, true, 'left');

        // Score to Beat
        let beatText = 'N/A';
        let beatScore = null;
        if (game.isRanked && game.rank > 1) {
          const nextRankEntry = game.leaderboard.find(e => e.rank === game.rank - 1);
          if (nextRankEntry) {
            beatScore = nextRankEntry.score;
            beatText = `${nextRankEntry.score.toLocaleString()} (Rank #${game.rank - 1})`;
            if (game.score) {
              beatText += ` (+${((nextRankEntry.score - game.score) / game.score * 100).toFixed(1)}% needed)`;
            }
          }
        } else if (!game.isRanked && game.leaderboard.length > 0) {
          const targetEntry = game.leaderboard.length >= 50 ? game.leaderboard[49] : game.leaderboard[game.leaderboard.length - 1];
          if (targetEntry) {
            beatScore = targetEntry.score;
            beatText = `${targetEntry.score.toLocaleString()} (Rank #${targetEntry.rank})`;
          }
        }
        if (beatScore !== null && beatScore !== topScoreEntry?.score) {
          textY += 130;
          ctx.fillStyle = '#A0AEC0';
          drawScaledText(ctx, 'SCORE TO BEAT:', rightX + 80, textY, rightW - 130, 24, false, 'left');
          ctx.fillStyle = '#F59E0B';
          drawScaledText(ctx, beatText, rightX + 80, textY + 45, rightW - 130, 36, true, 'left');
        }
      }

      ctx.restore();

      // Card border outline
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 10;
      drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 24);
      ctx.stroke();

      // Page Indicator
      if (totalCards > 0) {
        ctx.fillStyle = '#A0AEC0';
        ctx.font = '20px Jost, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`Card ${cardIndex + 1} of ${totalCards}`, width / 2, height - 115);
      }

      // Progress bar at the bottom
      const progressW = width - (margin * 2);
      ctx.fillStyle = '#1f2937';
      drawRoundedRect(ctx, margin, height - 90, progressW, 12, 6);
      ctx.fill();

      ctx.fillStyle = '#10B981';
      drawRoundedRect(ctx, margin, height - 90, progressW * progress, 12, 6);
      ctx.fill();

      // Footer
      ctx.fillStyle = '#718096';
      ctx.font = '22px Jost, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Created with FliPal Tournament Companion', width / 2, height - 40);

    } else {
      // ----------------------------------------------------
      // SQUARE LAYOUT (1:1 - Default)
      // ----------------------------------------------------
      const margin = 60;
      const cardX = margin;
      const cardY = 150; // Leave 150px for header
      const cardW = width - (margin * 2);
      const cardH = height - cardY - margin - 80; // 1080 - 150 - 60 - 80 = 790

      // Draw Top Overall Header
      ctx.fillStyle = 'white';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 6;
      drawScaledText(ctx, results?.title || 'Tournament', width / 2, 60, width - 80, 32, true, 'center');
      ctx.fillStyle = '#10B981';
      let subtitleText = `${username}  •  Overall: ${overallRank || '-'}`;
      if (dateRange) {
        subtitleText += `  •  ${dateRange}`;
      }
      drawScaledText(ctx, subtitleText, width / 2, 105, width - 80, 24, true, 'center');
      ctx.shadowBlur = 0;

      // Card border highlight based on status
      let borderColor = '#1f2937';
      if (game.isHighestRank) borderColor = '#10B981';
      else if (game.isLowestRank && game.isRanked) borderColor = '#F59E0B';
      else if (!game.isRanked) borderColor = '#EF4444';

      ctx.save();
      drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 24);
      ctx.clip();

      // Card Background
      ctx.fillStyle = '#121212';
      ctx.fillRect(cardX, cardY, cardW, cardH);

      // Image Area (Upper 55%)
      const imgH = Math.floor(cardH * 0.55);
      const imgUrl = game.image;
      const imgObj = loadedImagesRef.current[imgUrl];
      drawImageCover(ctx, imgObj, cardX, cardY, cardW, imgH);

      // Gradient overlay for titles
      const gradTop = ctx.createLinearGradient(cardX, cardY, cardX, cardY + 140);
      gradTop.addColorStop(0, 'rgba(0, 0, 0, 0.85)');
      gradTop.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradTop;
      ctx.fillRect(cardX, cardY, cardW, 140);

      const gradBot = ctx.createLinearGradient(cardX, cardY + imgH - 140, cardX, cardY + imgH);
      gradBot.addColorStop(0, 'rgba(0, 0, 0, 0)');
      gradBot.addColorStop(1, 'rgba(0, 0, 0, 0.85)');
      ctx.fillStyle = gradBot;
      ctx.fillRect(cardX, cardY + imgH - 140, cardW, 140);

      // Title
      ctx.fillStyle = 'white';
      drawScaledText(ctx, game.title, cardX + 35, cardY + 65, cardW - 70, 48, true, 'left');


      // Initials & Score Overlay
      if (game.isRanked) {
        ctx.fillStyle = '#10B981';
        drawScaledText(ctx, game.initials || '', cardX + 35, cardY + imgH - 30, 200, 36, true, 'left');
        if (game.score) {
          ctx.fillStyle = 'white';
          drawScaledText(ctx, game.score.toLocaleString(), cardX + cardW - (game.rank ? 180 : 35), cardY + imgH - 30, cardW - 250, 42, true, 'right');
        }
      }

      // Details section (Lower 45%)
      const detailsY = cardY + imgH;
      const detailsH = cardH - imgH;

      ctx.fillStyle = '#1c1c1e';
      ctx.fillRect(cardX + 35, detailsY + 20, cardW - 70, detailsH - 40);

      // Stats drawing inside container
      ctx.fillStyle = 'white';

      if (game.isRanked && game.rank === 1) {
        let textY = detailsY + 105;
        ctx.fillStyle = '#F59E0B'; // Gold
        drawScaledText(ctx, '🏆 GRAND CHAMPION', width / 2, textY, cardW - 140, 46, true, 'center');

        if (game.leaderboard.length > 1 && game.score) {
          ctx.fillStyle = '#10B981';
          const pctAhead = ((game.score - game.leaderboard[1].score) / game.leaderboard[1].score * 100).toFixed(1);
          drawScaledText(ctx, `(${pctAhead}% ahead)`, width / 2, textY + 45, cardW - 140, 28, true, 'center');
        }

        textY += 105;
        ctx.fillStyle = '#A0AEC0';
        drawScaledText(ctx, 'TOTAL PLAYERS:', width / 2, textY, cardW - 140, 24, false, 'center');
        ctx.fillStyle = 'white';
        drawScaledText(ctx, String(game.leaderboard.length), width / 2, textY + 38, cardW - 140, 36, true, 'center');
      } else {
        let textY = detailsY + 70;

        // Points & Players Row
        ctx.fillStyle = '#A0AEC0';
        drawScaledText(ctx, 'POINTS:', cardX + 70, textY, (cardW - 140) / 2, 24, false, 'left');
        drawScaledText(ctx, 'PLAYERS:', cardX + cardW / 2 + 20, textY, (cardW - 140) / 2, 24, false, 'left');
        
        const ptsVal = game.isRanked ? String(game.points ?? '-') : '-';
        ctx.fillStyle = '#10B981';
        drawScaledText(ctx, ptsVal, cardX + 70, textY + 38, (cardW - 140) / 2, 36, true, 'left');
        ctx.fillStyle = 'white';
        drawScaledText(ctx, String(game.leaderboard.length), cardX + cardW / 2 + 20, textY + 38, (cardW - 140) / 2, 36, true, 'left');

        textY += 95;

        // Top Score
        const topScoreEntry = game.leaderboard[0];
        ctx.fillStyle = '#A0AEC0';
        drawScaledText(ctx, 'TOP LEADERBOARD SCORE:', cardX + 70, textY, cardW - 140, 24, false, 'left');
        ctx.fillStyle = 'white';
        let topScoreText = 'N/A';
        if (topScoreEntry) {
          topScoreText = `${topScoreEntry.score.toLocaleString()} (${topScoreEntry.userName})`;
          if (game.isRanked && game.rank > 1 && game.score) {
            topScoreText += ` (+${((topScoreEntry.score - game.score) / game.score * 100).toFixed(1)}% needed)`;
          }
        }
        drawScaledText(ctx, topScoreText, cardX + 70, textY + 38, cardW - 140, 34, true, 'left');

        // Score to Beat
        let beatText = 'N/A';
        let beatScore = null;
        if (game.isRanked && game.rank > 1) {
          const nextRankEntry = game.leaderboard.find(e => e.rank === game.rank - 1);
          if (nextRankEntry) {
            beatScore = nextRankEntry.score;
            beatText = `${nextRankEntry.score.toLocaleString()} (Rank #${game.rank - 1})`;
            if (game.score) {
              beatText += ` (+${((nextRankEntry.score - game.score) / game.score * 100).toFixed(1)}% needed)`;
            }
          }
        } else if (!game.isRanked && game.leaderboard.length > 0) {
          const targetEntry = game.leaderboard.length >= 50 ? game.leaderboard[49] : game.leaderboard[game.leaderboard.length - 1];
          if (targetEntry) {
            beatScore = targetEntry.score;
            beatText = `${targetEntry.score.toLocaleString()} (Rank #${targetEntry.rank})`;
          }
        }
        if (beatScore !== null && beatScore !== topScoreEntry?.score) {
          textY += 95;
          ctx.fillStyle = '#A0AEC0';
          drawScaledText(ctx, 'SCORE TO BEAT:', cardX + 70, textY, cardW - 140, 24, false, 'left');
          ctx.fillStyle = '#F59E0B';
          drawScaledText(ctx, beatText, cardX + 70, textY + 38, cardW - 140, 34, true, 'left');
        }
      }

      // Rank Circular Badge (Moved here to draw on top of details container and raised slightly)
      if (game.isRanked && game.rank) {
        const badgeX = cardX + cardW - 90;
        const badgeY = cardY + imgH - 60;
        
        ctx.shadowColor = 'rgba(0,0,0,0.6)';
        ctx.shadowBlur = 8;
        
        ctx.fillStyle = '#10B981';
        ctx.beginPath();
        ctx.arc(badgeX, badgeY, 65, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 5;
        ctx.stroke();
        
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'white';
        drawScaledText(ctx, `#${game.rank}`, badgeX, badgeY + 20, 100, 56, true, 'center');
      }

      ctx.restore();

      // Card border
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 10;
      drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 24);
      ctx.stroke();

      // Page Indicator
      if (totalCards > 0) {
        ctx.fillStyle = '#A0AEC0';
        ctx.font = '20px Jost, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`Card ${cardIndex + 1} of ${totalCards}`, width / 2, height - 115);
      }

      // Progress bar at the bottom
      const progressW = width - (margin * 2);
      ctx.fillStyle = '#1f2937';
      drawRoundedRect(ctx, margin, height - 90, progressW, 12, 6);
      ctx.fill();

      ctx.fillStyle = '#10B981';
      drawRoundedRect(ctx, margin, height - 90, progressW * progress, 12, 6);
      ctx.fill();

      // Footer
      ctx.fillStyle = '#718096';
      ctx.font = '22px Jost, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Created with FliPal Tournament Companion', width / 2, height - 40);
    }
  }, [aspectRatio, results, username, overallRank]);

  // Main animation frame handler
  const animate = useCallback((time) => {
    if (prevTimeRef.current === null) {
      prevTimeRef.current = time;
    }
    const elapsed = time - prevTimeRef.current;
    prevTimeRef.current = time;

    const activeGames = activeGamesRef.current;
    if (activeGames.length === 0) return;

    cardElapsedRef.current += elapsed;

    if (cardElapsedRef.current >= slideDuration) {
      cardElapsedRef.current = 0;
      setCurrentIndex(prev => (prev + 1) % activeGames.length);
    }

    const currentSlideProgress = cardElapsedRef.current / slideDuration;

    const canvas = canvasRef.current;
    if (canvas) {
      const { w, h } = getCanvasDimensions(aspectRatio);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      const ctx = canvas.getContext('2d');

      // Setup offscreen canvases for blending if fading
      if (activeGames.length > 1 && cardElapsedRef.current > (slideDuration - transitionDuration)) {
        const fadeProgress = (cardElapsedRef.current - (slideDuration - transitionDuration)) / transitionDuration;
        const nextIndex = (currentIndex + 1) % activeGames.length;

        // Render Slide 1
        if (!offscreenCanvas1.current) offscreenCanvas1.current = document.createElement('canvas');
        offscreenCanvas1.current.width = w;
        offscreenCanvas1.current.height = h;
        renderSingleCard(offscreenCanvas1.current.getContext('2d'), activeGames[currentIndex], w, h, currentSlideProgress, currentIndex, activeGames.length);

        // Render Slide 2
        if (!offscreenCanvas2.current) offscreenCanvas2.current = document.createElement('canvas');
        offscreenCanvas2.current.width = w;
        offscreenCanvas2.current.height = h;
        renderSingleCard(offscreenCanvas2.current.getContext('2d'), activeGames[nextIndex], w, h, 0, nextIndex, activeGames.length);

        // Draw blended slides on main canvas
        ctx.clearRect(0, 0, w, h);
        ctx.globalAlpha = 1 - fadeProgress;
        ctx.drawImage(offscreenCanvas1.current, 0, 0);
        ctx.globalAlpha = fadeProgress;
        ctx.drawImage(offscreenCanvas2.current, 0, 0);
        ctx.globalAlpha = 1;
      } else {
        renderSingleCard(ctx, activeGames[currentIndex], w, h, currentSlideProgress, currentIndex, activeGames.length);
      }
    }

    requestRef.current = requestAnimationFrame(animate);
  }, [aspectRatio, currentIndex, renderSingleCard, slideDuration]);

  // Restart frame loop when aspect ratio, index, active games, or play state changes
  useEffect(() => {
    if (loading || isRecording) return;

    if (isPlaying) {
      requestRef.current = requestAnimationFrame(animate);
    } else {
      // Just render static frame
      const canvas = canvasRef.current;
      const activeGames = activeGamesRef.current;
      if (canvas && activeGames.length > 0) {
        const { w, h } = getCanvasDimensions(aspectRatio);
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
        }
        const ctx = canvas.getContext('2d');
        renderSingleCard(ctx, activeGames[currentIndex], w, h, cardElapsedRef.current / slideDuration, currentIndex, activeGames.length);
      }
    }

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [isPlaying, currentIndex, aspectRatio, loading, isRecording, animate, renderSingleCard]);

  // Handle slide selection from list/dropdown
  const jumpToSlide = (index) => {
    setCurrentIndex(index);
    cardElapsedRef.current = 0;
    prevTimeRef.current = null;
    
    // Trigger redraw
    const canvas = canvasRef.current;
    const activeGames = activeGamesRef.current;
    if (canvas && activeGames.length > 0) {
      const { w, h } = getCanvasDimensions(aspectRatio);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      const ctx = canvas.getContext('2d');
      renderSingleCard(ctx, activeGames[index], w, h, 0, index, activeGames.length);
    }
  };

  // Helper to find a supported video recording mimetype
  const getSupportedMimeType = () => {
    const types = [
      'video/mp4;codecs=h264',
      'video/mp4;codecs=avc1',
      'video/mp4',
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/quicktime'
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return '';
  };

  // Export current frame as static image
  const saveCurrentFrame = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const activeGames = activeGamesRef.current;
    const currentTitle = activeGames[currentIndex]?.title || 'game';
    const link = document.createElement('a');
    link.download = `${username}-${currentTitle.toLowerCase().replace(/[^a-z0-9]/g, '-')}-card.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Export entire animation sequence as video
  const exportVideo = async () => {
    const activeGames = activeGamesRef.current;
    if (activeGames.length === 0) return;

    // Pause normal preview loop
    setIsPlaying(false);
    if (requestRef.current) {
      cancelAnimationFrame(requestRef.current);
    }

    setIsRecording(true);
    setRecordProgress(0);

    const canvas = canvasRef.current;
    const { w, h } = getCanvasDimensions(aspectRatio);
    canvas.width = w;
    canvas.height = h;

    // Prepare stream and recorder
    const stream = canvas.captureStream(30); // 30 fps
    const mimeType = getSupportedMimeType();
    
    if (!mimeType) {
      alert('Video export is not supported in this browser.');
      setIsRecording(false);
      setIsPlaying(true);
      return;
    }

    const chunks = [];
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: 2500000 // 2.5 Mbps
    });

    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      let ext = 'webm';
      if (mimeType.includes('mp4')) {
        ext = 'mp4';
      } else if (mimeType.includes('quicktime')) {
        ext = 'mov';
      }
      link.download = `${username}-tournament-animation.${ext}`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);

      setIsRecording(false);
      setIsPlaying(true);
    };

    mediaRecorder.start();

    // Simulated frame-by-frame rendering for smooth encoding
    const fps = 30;
    const frameInterval = 1000 / fps;
    const totalDuration = slideDuration * activeGames.length;
    let simulatedTime = 0;

    const renderRecordingFrame = () => {
      if (simulatedTime >= totalDuration) {
        mediaRecorder.stop();
        return;
      }

      const activeIndex = Math.floor(simulatedTime / slideDuration) % activeGames.length;
      const cardElapsed = simulatedTime % slideDuration;
      const slideProgress = cardElapsed / slideDuration;

      const ctx = canvas.getContext('2d');

      // Draw active frame with simulated time
      if (activeGames.length > 1 && cardElapsed > (slideDuration - transitionDuration)) {
        const fadeProgress = (cardElapsed - (slideDuration - transitionDuration)) / transitionDuration;
        const nextIndex = (activeIndex + 1) % activeGames.length;

        // Render Slide 1
        if (!offscreenCanvas1.current) offscreenCanvas1.current = document.createElement('canvas');
        offscreenCanvas1.current.width = w;
        offscreenCanvas1.current.height = h;
        renderSingleCard(offscreenCanvas1.current.getContext('2d'), activeGames[activeIndex], w, h, slideProgress, activeIndex, activeGames.length);

        // Render Slide 2
        if (!offscreenCanvas2.current) offscreenCanvas2.current = document.createElement('canvas');
        offscreenCanvas2.current.width = w;
        offscreenCanvas2.current.height = h;
        renderSingleCard(offscreenCanvas2.current.getContext('2d'), activeGames[nextIndex], w, h, 0, nextIndex, activeGames.length);

        // Render blended slides
        ctx.clearRect(0, 0, w, h);
        ctx.globalAlpha = 1 - fadeProgress;
        ctx.drawImage(offscreenCanvas1.current, 0, 0);
        ctx.globalAlpha = fadeProgress;
        ctx.drawImage(offscreenCanvas2.current, 0, 0);
        ctx.globalAlpha = 1;
      } else {
        // Just draw card directly
        ctx.save();
        renderSingleCard(ctx, activeGames[activeIndex], w, h, slideProgress, activeIndex, activeGames.length);
        ctx.restore();
      }

      // Update progress
      simulatedTime += frameInterval;
      setRecordProgress(Math.floor((simulatedTime / totalDuration) * 100));

      // Continue recording loop
      setTimeout(renderRecordingFrame, 16); // ~60fps paint throttle
    };

    // Run recording loop
    renderRecordingFrame();
  };


  const { w: canvasW, h: canvasH } = getCanvasDimensions(aspectRatio);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Save Card Animation"
      size="xlarge"
    >
      <div className={`animation-modal-container ${theme}`}>
        {loading && (
          <div className="animation-loading">
            <span className="spinner"><i className="fa-solid fa-spinner fa-spin"></i></span>
            Loading and pre-rendering game card assets...
          </div>
        )}
        
        {error && (
          <div className="animation-error">{error}</div>
        )}
        
        {!loading && !error && activeGames.length === 0 && (
          <div className="animation-empty">
            No game cards fit the selected criteria. Try checking your settings or filters.
          </div>
        )}

        {!loading && !error && activeGames.length > 0 && (
          <div className="animation-workspace">
            {/* Left Column: Canvas Preview */}
            <div className="animation-preview-column">
              <div className="canvas-wrapper" style={{ aspectRatio: `${canvasW}/${canvasH}` }}>
                <canvas 
                  ref={canvasRef} 
                  width={canvasW} 
                  height={canvasH}
                  className="animation-canvas"
                />
                
                {isRecording && (
                  <div className="recording-overlay">
                    <div className="recording-content">
                      <div className="recording-title">
                        <i className="fa-solid fa-circle-dot fa-fade" style={{ color: '#ef4444', marginRight: '8px' }}></i>
                        Recording Video...
                      </div>
                      <div className="recording-bar-bg">
                        <div className="recording-bar-fill" style={{ width: `${recordProgress}%` }} />
                      </div>
                      <div className="recording-percentage">{recordProgress}%</div>
                      <div className="recording-subtitle">Please keep this tab open and active</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Playback Controls */}
              <div className="playback-controls">
                <button className="control-btn" onClick={restartSlideshow} title="Restart" disabled={isRecording}>
                  <i className="fa-solid fa-backward-step"></i>
                </button>
                <button className="control-btn play-btn" onClick={togglePlay} title={isPlaying ? 'Pause' : 'Play'} disabled={isRecording}>
                  {isPlaying ? <i className="fa-solid fa-pause"></i> : <i className="fa-solid fa-play"></i>}
                </button>
                <div className="playback-timeline-text">
                  Card {currentIndex + 1} of {activeGames.length}
                </div>
                <select 
                  className="card-select"
                  value={currentIndex}
                  onChange={(e) => jumpToSlide(parseInt(e.target.value))}
                  disabled={isRecording}
                >
                  {activeGames.map((game, idx) => (
                    <option key={idx} value={idx}>
                      {idx + 1}. {game.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Right Column: Settings & Export Controls */}
            <div className="animation-settings-column">
              <div className="settings-section">
                <h3>Design & Sort Settings</h3>
                
                {/* Aspect Ratio */}
                <div className="setting-item">
                  <label htmlFor="aspect-ratio-select">Aspect Ratio:</label>
                  <select 
                    id="aspect-ratio-select"
                    value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value)}
                    disabled={isRecording}
                  >
                    <option value="square">Square (1:1 - Instagram/Feed)</option>
                    <option value="stories">Stories (9:16 - Reels/TikTok)</option>
                    <option value="landscape">Landscape (16:9 - YouTube/Widescreen)</option>
                  </select>
                </div>

                {/* Sort Order */}
                <div className="setting-item">
                  <label htmlFor="sort-order-select">Sort Order:</label>
                  <select 
                    id="sort-order-select"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    disabled={isRecording || orderByHighestRank}
                  >
                    <option value="default">Default (as loaded)</option>
                    <option value="rank">By Rank (best first)</option>
                    <option value="alpha">Alphabetical</option>
                  </select>
                </div>

                {/* Order by Highest Rank Opt-in */}
                <div className="setting-item checkbox-item">
                  <label className="checkbox-container">
                    <input 
                      type="checkbox" 
                      checked={orderByHighestRank}
                      onChange={(e) => setOrderByHighestRank(e.target.checked)}
                      disabled={isRecording}
                    />
                    <span className="checkbox-checkmark"></span>
                    Order by Highest Rank
                  </label>
                </div>

                {/* Filters */}
                <div className="setting-item checkbox-item">
                  <label className="checkbox-container">
                    <input 
                      type="checkbox" 
                      checked={showRankedOnly}
                      onChange={(e) => setShowRankedOnly(e.target.checked)}
                      disabled={isRecording}
                    />
                    <span className="checkbox-checkmark"></span>
                    Show Ranked Games Only
                  </label>
                </div>
              </div>

              {/* Export Controls */}
              <div className="export-section">
                <h3>Export & Save Actions</h3>
                <p className="export-explanation">
                  Create a looping video of all cards showing for 3 seconds each, or save a high-res image of the currently displayed card.
                </p>

                <button 
                  className="export-button video-export"
                  onClick={exportVideo}
                  disabled={isRecording}
                >
                  <i className={isRecording ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-video"}></i>
                  <span>{isRecording ? `Encoding Video (${recordProgress}%)` : 'Save Animation (Video)'}</span>
                </button>

                <button 
                  className="export-button image-export"
                  onClick={saveCurrentFrame}
                  disabled={isRecording}
                >
                  <i className="fa-solid fa-image"></i>
                  <span>Save Current Frame (Image)</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default GameCardAnimationModal;
